import {
  Fragment,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { BackIcon, CheckIcon, ChevronDownIcon, ForwardIcon } from "./icons.tsx";
import { PendingCount } from "./pending-count.tsx";

/** `short` is what the closed button shows when the full label would crowd a narrow pane. */
export type MenuOption<T extends string> = {
  value: T;
  label: string;
  short?: string;
  detail?: string;
  /**
   * A total beside the label, read with its noun: the workspace switcher's needs-reply replies. "pending"
   * holds its place with a bar while it is on its way.
   */
  count?: { value: number; noun: string } | "pending";
  /** A mark before the words, such as a label's key dot (DESIGN.md, the Label Key Rule). */
  mark?: ReactNode;
  /** One key that chooses this while the menu is open, drawn as a keycap where keys are shown. */
  shortcut?: string;
  /** The current one in a menu of commands, which ticks nothing: the menu opens on it. */
  current?: boolean;
  /** A hairline above it: the first row of a group, as the panel parts its own (LEA-501). */
  ruleBefore?: boolean;
};

/**
 * A row that opens more choices in the menu's own place, one level down: the Status filter's Reply
 * agent. It is a way in, never a choice itself; `key` tells it from the menu's other rows.
 *
 * With its own `value` it is a second choice in the same menu, made beside the menu's own (the
 * agent state beside the master status): its first option puts it back, and any other is named on
 * the group's row. `open` opens the menu in it.
 */
type MenuGroup<T extends string> = {
  key: string;
  label: string;
  detail?: string;
  /** A mark before the words, as a choice's. */
  mark?: ReactNode;
  /** A hairline above it, as a choice's. */
  ruleBefore?: boolean;
  submenu: readonly MenuOption<T>[];
  value?: T;
  open?: boolean;
};

export type MenuRow<T extends string> = MenuOption<T> | MenuGroup<T>;

const isGroup = <T extends string>(row: MenuRow<T>): row is MenuGroup<T> => "submenu" in row;

/** The value a group's rows are ticked against: its own, else the menu's. */
const groupValue = <T extends string>(group: MenuGroup<T>, menu: T | ""): T | "" =>
  group.value ?? menu;

/** The choice a group's row names: the one it holds, unless that is its own first, which puts it back. */
function heldIn<T extends string>(group: MenuGroup<T>, menu: T | ""): MenuOption<T> | undefined {
  const held = group.submenu.find((option) => option.value === groupValue(group, menu));
  return group.value !== undefined && held === group.submenu[0] ? undefined : held;
}

/** The choice with this value, in a group or not: what a closed button names. */
export function menuChoice<T extends string>(
  rows: readonly MenuRow<T>[],
  value: T | "",
): MenuOption<T> | undefined {
  return rows
    .flatMap((row) => (isGroup(row) ? row.submenu : [row]))
    .find((option) => option.value === value);
}

/*
 * A button that opens its choices in place, instead of a native select. One choice is current; the
 * menu opens on it, arrows and Home/End move, Enter or a click chooses, and Escape, a choice or a
 * click outside closes it and returns focus to the button.
 *
 * The inbox's More control is the same menu with no choice current until one of its groups is
 * chosen: `display` is what the closed button says, and `pressed` marks it as holding the choice.
 *
 * With `actions` it is a menu of commands rather than choices (a saved view's Rename and Delete):
 * its items are plain menu items with no tick, none is current, and the closed button can be its
 * icon alone (`caret` false, `display` null).
 *
 * A row with a `submenu` leads to more choices, shown in the list's place rather than as a flyout
 * beside it, which a 390 px phone has no room for. ArrowRight, Enter, Space or a click goes in; the
 * first row inside goes back and is named by the menu's label. ArrowLeft or Escape also goes back,
 * to the group's row, and a second Escape closes. A menu whose current choice is in a group opens
 * there.
 */
export function OptionMenu<T extends string>(props: {
  label: string;
  icon: ReactNode;
  value: T | "";
  options: readonly MenuRow<T>[];
  onChange: (value: T) => void;
  display?: ReactNode;
  pressed?: boolean;
  className?: string;
  actions?: boolean;
  caret?: boolean;
  /** A keycap after the closed button's value, for a menu a key also opens (desktop only). */
  keycap?: ReactNode;
  /** The key that opens it, for assistive technology, where keys are shown. */
  keyshortcuts?: string;
  /** What the closed button is read as holding, where a group's own choice joins the menu's. */
  summary?: string;
  /** Open from outside too (a key), when given; otherwise the menu keeps its own state. */
  open?: boolean;
  /** Shown but not to be opened: the selection bar's More while nothing is ticked yet. */
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Said under the top list's rows, never chosen: choices still on their way, or that none exist. */
  after?: ReactNode;
  /**
   * A desktop with keys shown: each option's `shortcut` chooses it while the menu is open and sits
   * on its row as a keycap. Off (a phone, a touch screen) neither is there.
   */
  showKeys?: boolean;
}) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = props.open ?? ownOpen;
  const changed = useRef(props.onOpenChange);
  changed.current = props.onOpenChange;
  const setOpen = (next: boolean): void => {
    setOwnOpen(next);
    changed.current?.(next);
  };
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const current = menuChoice(props.options, props.value);
  const named = props.summary ?? current?.label;

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target)) return;
      setOwnOpen(false);
      changed.current?.(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);

  const close = (): void => {
    setOpen(false);
    button.current?.focus();
  };

  return (
    <div className={`lcr-option-menu${props.className ? ` ${props.className}` : ""}`} ref={root}>
      <button
        ref={button}
        type="button"
        className="lcr-option-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={named ? `${props.label}: ${named}` : props.label}
        aria-keyshortcuts={props.keyshortcuts}
        data-pressed={props.pressed || undefined}
        disabled={props.disabled === true}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key !== "ArrowDown" || open) return;
          event.preventDefault();
          setOpen(true);
        }}
      >
        {props.icon}
        <span className="lcr-option-value">
          {props.display ?? current?.short ?? current?.label}
        </span>
        {props.caret !== false && <ChevronDownIcon size={14} className="lcr-option-caret" />}
        {props.keycap}
      </button>
      {open && (
        <MenuList
          id={menuId}
          label={props.label}
          value={props.value}
          options={props.options}
          actions={props.actions === true}
          after={props.after}
          showKeys={props.showKeys === true}
          onChoose={(value) => {
            props.onChange(value);
            close();
          }}
          onClose={close}
        />
      )}
    </div>
  );
}

const rowsIn = (list: HTMLElement | null): HTMLButtonElement[] => [
  ...(list?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]') ?? []),
];

/** Where an arrow, Home or End takes the focus among a list's rows, wrapping at either end. */
function stepTarget(
  key: string,
  items: readonly HTMLButtonElement[],
  index: number,
): HTMLButtonElement | undefined {
  switch (key) {
    case "ArrowDown":
      return items[(index + 1) % items.length];
    case "ArrowUp":
      return items[(index - 1 + items.length) % items.length];
    case "Home":
      return items[0];
    case "End":
      return items.at(-1);
    default:
      return undefined;
  }
}

/** The choice a key names by its `shortcut`, pressed alone. */
function shortcutChoice<T extends string>(
  rows: readonly MenuRow<T>[],
  event: React.KeyboardEvent,
): MenuOption<T> | undefined {
  if (event.metaKey || event.ctrlKey || event.altKey) return undefined;
  return rows.find((row): row is MenuOption<T> => !isGroup(row) && row.shortcut === event.key);
}

/**
 * The open menu: the top list or one group's, never both. It mounts with each opening, so it opens
 * where the current choice is and forgets where it was when closed.
 */
function MenuList<T extends string>(props: {
  id: string;
  label: string;
  value: T | "";
  options: readonly MenuRow<T>[];
  actions: boolean;
  after: ReactNode;
  showKeys: boolean;
  onChoose: (value: T) => void;
  onClose: () => void;
}) {
  const list = useRef<HTMLDivElement>(null);
  const [inside, setInside] = useState<string | null>(
    () =>
      props.options.find(
        (row): row is MenuGroup<T> =>
          isGroup(row) &&
          (row.open === true || row.submenu.some((option) => option.value === props.value)),
      )?.key ?? null,
  );
  // The group just left, whose row takes the focus back.
  const left = useRef<string | null>(null);
  const group = props.options.find(
    (row): row is MenuGroup<T> => isGroup(row) && row.key === inside,
  );
  const rows = group ? group.submenu : props.options;
  const ticked = group ? groupValue(group, props.value) : props.value;
  // Counts and keycaps line up in one column only if every row keeps the tick's room, ticked or not.
  const keyed = props.showKeys && rows.some((row) => !isGroup(row) && row.shortcut);
  const counted = keyed || rows.some((row) => !isGroup(row) && row.count);
  const itemRole = props.actions ? "menuitem" : "menuitemradio";

  useLayoutEffect(() => {
    const items = rowsIn(list.current);
    const door =
      inside === null ? items.find((item) => item.dataset["group"] === left.current) : undefined;
    left.current = null;
    (
      door ??
      items.find((item) => item.getAttribute("aria-checked") === "true") ??
      items.find((item) => item.hasAttribute("data-current")) ??
      items.find((item) => !item.hasAttribute("data-back"))
    )?.focus();
  }, [inside]);

  const back = (): void => {
    left.current = inside;
    setInside(null);
  };
  // A choice's own key (the label menu's 1 to 8) chooses it, where keys are shown.
  const chooseByKey = (event: React.KeyboardEvent<HTMLDivElement>): boolean => {
    const chosen = keyed ? shortcutChoice(rows, event) : undefined;
    if (!chosen) return false;
    event.preventDefault();
    event.stopPropagation();
    props.onChoose(chosen.value);
    return true;
  };
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (group && (event.key === "Escape" || event.key === "ArrowLeft")) {
      event.preventDefault();
      back();
      return;
    }
    if (event.key === "Escape" || event.key === "Tab") {
      if (event.key === "Escape") event.preventDefault();
      props.onClose();
      return;
    }
    const items = rowsIn(list.current);
    const active = items.find((item) => item === document.activeElement);
    const target = stepTarget(event.key, items, active ? items.indexOf(active) : -1);
    if (!target) return;
    event.preventDefault();
    target.focus();
  };

  return (
    <div
      ref={list}
      className="lcr-popover lcr-option-popover"
      id={props.id}
      role="menu"
      aria-label={group ? group.label : props.label}
      onKeyDown={(event) => chooseByKey(event) || onKeyDown(event)}
    >
      {group && (
        <BackRow
          label={props.label}
          shown={props.actions ? group.label : props.label}
          onBack={back}
        />
      )}
      {!props.actions && <p className="lcr-label">{group ? group.label : props.label}</p>}
      {rows.map((row) => (
        <Fragment key={isGroup(row) ? `group:${row.key}` : row.value}>
          {row.ruleBefore && <hr className="lcr-view-menu-rule" />}
          {isGroup(row) ? (
            <GroupRow
              group={row}
              holding={heldIn(row, props.value)}
              onOpen={() => setInside(row.key)}
            />
          ) : (
            <ChoiceRow
              option={row}
              role={itemRole}
              checked={props.actions ? undefined : row.value === ticked}
              counted={counted}
              showKeys={keyed}
              onChoose={props.onChoose}
            />
          )}
        </Fragment>
      ))}
      {!group && props.after}
    </div>
  );
}

function RowText(props: { label: string; detail: string | undefined }) {
  return (
    <span className="lcr-menu-item-text" title={props.label}>
      <span className="lcr-menu-item-label">{props.label}</span>
      {props.detail && <span className="lcr-menu-item-detail">{props.detail}</span>}
    </span>
  );
}

function ChoiceRow<T extends string>(props: {
  option: MenuOption<T>;
  role: "menuitem" | "menuitemradio";
  /** Undefined in a menu of commands, where nothing is current. */
  checked: boolean | undefined;
  counted: boolean;
  showKeys: boolean;
  onChoose: (value: T) => void;
}) {
  const { option, checked } = props;
  const shortcut = props.showKeys ? option.shortcut : undefined;
  return (
    <button
      type="button"
      role={props.role}
      {...(checked === undefined ? {} : { "aria-checked": checked })}
      {...(shortcut ? { "aria-keyshortcuts": shortcut } : {})}
      {...(option.current ? { "data-current": "" } : {})}
      className="lcr-menu-item"
      onClick={() => props.onChoose(option.value)}
    >
      {option.mark}
      <RowText label={option.label} detail={option.detail} />
      {shortcut && (
        <span className="lcr-kbd lcr-menu-item-key" aria-hidden="true">
          {shortcut}
        </span>
      )}
      {option.count === "pending" && <PendingCount className="lcr-menu-item-count" />}
      {option.count && option.count !== "pending" && (
        <span className="lcr-menu-item-count">
          <span aria-hidden="true">{option.count.value.toLocaleString("en-GB")}</span>
          <span className="lcr-visually-hidden">
            , {option.count.value.toLocaleString("en-GB")} {option.count.noun}
          </span>
        </span>
      )}
      {checked === undefined ? null : checked ? (
        <CheckIcon size={16} />
      ) : (
        props.counted && <span className="lcr-menu-item-tick-room" aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * A group's row: its chevron stands where a choice's tick would, so the column holds. While the
 * current choice is inside, the row is shaded as a ticked one is and names that choice under it.
 */
function GroupRow<T extends string>(props: {
  group: MenuGroup<T>;
  holding: MenuOption<T> | undefined;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      aria-haspopup="menu"
      data-group={props.group.key}
      data-holding={props.holding !== undefined || undefined}
      className="lcr-menu-item"
      // Enter and Space are the button's own: its click, which Firefox fires on Space's keyup, after
      // the focus has moved. Taken on keydown, that click would land on the row the focus moved to.
      onClick={props.onOpen}
      onKeyDown={(event) => {
        if (event.key !== "ArrowRight") return;
        event.preventDefault();
        props.onOpen();
      }}
    >
      {props.group.mark}
      <RowText label={props.group.label} detail={props.holding?.label ?? props.group.detail} />
      <ForwardIcon size={16} className="lcr-menu-item-go" />
    </button>
  );
}

/** The first row inside a group, back to the list it was opened from, named by the menu. */
/**
 * In a menu of commands the row is headed by the group it leads back from ("‹ Assign"), since no
 * label heads the list under it; it still names the menu it returns to.
 */
function BackRow(props: { label: string; shown: string; onBack: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      data-back=""
      aria-label={`Back to ${props.label}`}
      className="lcr-menu-item lcr-menu-back"
      onClick={props.onBack}
    >
      <BackIcon size={16} />
      {props.shown}
    </button>
  );
}
